/**
 * Resolving AI-proposed ownership events against the catalogue.
 *
 * `enrichParentOrgOwnershipEvents` returns a company NAME, never an id — a model
 * cannot know a document slug, and `deriveBrandSlug` strips accents, so every
 * plausible guess ("nomos-glashutte") names a document that does not exist. This
 * module does the matching the model cannot, and is pure so the matching rules
 * are testable without a catalogue fetch or a dialog.
 *
 * `ParentOrgOwnershipEvent.targetId` is required, so an event whose target does
 * not resolve CANNOT be written. Such an event is returned with
 * `targetId: null` and the dialog blocks it until a curator picks a target by
 * hand. Guessing here would write a foreign key to the wrong company.
 */
import type { AIOwnershipEvent } from '../../services/firebase';
import type { ParentOrgOwnershipEvent } from '../../types';

/** One catalogue entry a proposed name can match against. */
export interface ResolvableEntity {
  id: string;
  name?: string;
  displayName?: string;
  alternativeNames?: string[];
  abbreviations?: string[];
}

/** The three catalogues, keyed by the `targetType` that selects them. */
export interface ResolveCatalogues {
  brand: ResolvableEntity[];
  manufacturer: ResolvableEntity[];
  parentOrg: ResolvableEntity[];
}

/**
 * Comparison key: accent-folded, case-folded, punctuation-stripped, spaces
 * collapsed. "Nomos Glashütte" and "nomos glashutte" match; "A. Lange & Söhne"
 * and "A Lange and Sohne" do not, and deliberately so — `and` is a word, not
 * punctuation, and pretending otherwise starts matching genuinely different
 * companies.
 */
export function entityMatchKey(value: string): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Every name one catalogue entry answers to. */
function keysFor(entity: ResolvableEntity): string[] {
  return [
    entity.id,
    entity.name,
    entity.displayName,
    ...(entity.alternativeNames ?? []),
    ...(entity.abbreviations ?? []),
  ]
    .map((v) => entityMatchKey(v ?? ''))
    .filter(Boolean);
}

export interface ResolvedTarget {
  /** The matched entity's id, or null when the name matched nothing usable. */
  id: string | null;
  /** How it matched — for the dialog to show, and for a curator to distrust. */
  via: 'exact' | 'ambiguous' | 'none';
  /** Every candidate, when more than one entity answers to the name. */
  candidateIds: string[];
}

/**
 * Resolve one proposed name within one catalogue.
 *
 * An AMBIGUOUS name — two catalogue entries answering to it — resolves to
 * nothing on purpose. Two brands called "Eterna" is exactly the case where
 * picking the first would attach an acquisition to the wrong company, and the
 * dialog can offer the candidates instead.
 */
export function resolveTargetName(
  name: string,
  catalogue: readonly ResolvableEntity[],
): ResolvedTarget {
  const key = entityMatchKey(name);
  if (!key) return { id: null, via: 'none', candidateIds: [] };

  const matches = catalogue.filter((entity) => keysFor(entity).includes(key));
  if (matches.length === 1) return { id: matches[0].id, via: 'exact', candidateIds: [matches[0].id] };
  if (matches.length > 1) {
    return { id: null, via: 'ambiguous', candidateIds: matches.map((m) => m.id) };
  }
  return { id: null, via: 'none', candidateIds: [] };
}

/** An AI event with its targets resolved as far as the catalogue allows. */
export interface ResolvedOwnershipEvent {
  event: AIOwnershipEvent;
  target: ResolvedTarget;
  /** Only resolved when the model proposed a previous owner. */
  previousOwner: ResolvedTarget | null;
  /** True when this event already exists on the document (same date + target). */
  duplicate: boolean;
}

/**
 * Resolve a batch, marking any event the organization already records.
 *
 * Duplicate detection keys on date + target id, not on the description: the
 * prompt asks the model to EXTEND rather than restate, but a re-run after a
 * partial apply will legitimately re-propose what is already stored, and
 * offering those pre-ticked is how a timeline grows two copies of one deal.
 */
export function resolveOwnershipEvents(
  events: readonly AIOwnershipEvent[],
  catalogues: ResolveCatalogues,
  existing: readonly ParentOrgOwnershipEvent[] = [],
): ResolvedOwnershipEvent[] {
  const existingKeys = new Set(
    existing.map((e) => `${entityMatchKey(e.date)}::${e.targetId}`),
  );
  return events.map((event) => {
    const target = resolveTargetName(event.targetName, catalogues[event.targetType] ?? []);
    const previousOwner = event.previousOwnerName
      ? resolveTargetName(event.previousOwnerName, catalogues.parentOrg)
      : null;
    const duplicate =
      target.id !== null && existingKeys.has(`${entityMatchKey(event.date)}::${target.id}`);
    return { event, target, previousOwner, duplicate };
  });
}

/**
 * Turn a resolved event into the record the document stores.
 *
 * Returns null when the target is still unresolved — the FK is required, and an
 * event without one is not writable. `previousOwnerId` is dropped when it did
 * not resolve rather than blocking the event: it is optional, and losing an
 * unverifiable seller is cheaper than losing a verified acquisition.
 */
export function toStoredEvent(
  resolved: ResolvedOwnershipEvent,
  id: string,
  overrides?: { targetId?: string; previousOwnerId?: string },
): ParentOrgOwnershipEvent | null {
  const targetId = overrides?.targetId ?? resolved.target.id;
  if (!targetId) return null;
  const previousOwnerId = overrides?.previousOwnerId ?? resolved.previousOwner?.id ?? undefined;
  const { event } = resolved;
  return {
    id,
    date: event.date,
    type: event.type,
    targetType: event.targetType,
    targetId,
    ...(previousOwnerId ? { previousOwnerId } : {}),
    ...(event.description ? { description: event.description } : {}),
    ...(event.sourceUrl ? { sourceUrl: event.sourceUrl } : {}),
    ...(typeof event.aiConfidence === 'number' ? { aiConfidence: event.aiConfidence } : {}),
  };
}
